import { logger } from 'src/logger';
import { v4 as uuid } from 'uuid';

interface SessionState {
  didTransform: boolean;
  didDownload: boolean;
  didCreateCampaign: boolean;
  transformationCount: number;
  transformationVariants: string[];
  downloadCount: number;
  emitSequence: number;
  campaignId?: string;
}

const initialState = (): SessionState => ({
  didTransform: false,
  didDownload: false,
  didCreateCampaign: false,
  transformationCount: 0,
  transformationVariants: [],
  downloadCount: 0,
  emitSequence: 0,
});

class SessionContextSingleton {
  private sessionId = '';
  private state: SessionState = initialState();
  private userId = '';
  private marketplace = '';

  init(): void {
    this.sessionId = uuid();
    this.state = initialState();

    try {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.emitSessionLog('TAB_CLOSE');
        }
      });
    } catch {
      // SSR or test environment — ignore
    }
  }

  getSessionId(): string {
    return this.sessionId;
  }

  setUserId(userId: string): void {
    this.userId = userId;
  }

  setMarketplace(marketplace: string): void {
    this.marketplace = marketplace;
  }

  update(fields: Partial<SessionState>): void {
    this.state = { ...this.state, ...fields };
  }

  getState(): Readonly<SessionState> {
    return this.state;
  }

  emitSessionLog(trigger: string): void {
    this.emitCanonicalLog('SESSION', { trigger, ...this.state });
  }

  emitCanonicalLog(logType: string, fields: Record<string, unknown>): void {
    try {
      this.state.emitSequence += 1;
      logger.info('CANONICAL_LOG', {
        canonical: true,
        logType,
        sessionId: this.sessionId,
        userId: this.userId,
        marketplace: this.marketplace,
        seq: this.state.emitSequence,
        timestamp: new Date().toISOString(),
        ...fields,
      });
    } catch {
      // Never let logging break the app
    }
  }
}

export const SessionContext = new SessionContextSingleton();
